import { faTimesCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionModal, FormError, FormLayout } from '@kcftech/design-system';
import {
    AccessLevel,
    CorporationCreateRequest,
    CorporationResponse,
    FileParameter,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { FileUploadButton } from 'components/button/FileUploadButton';
import { Select } from 'components/forms/Select';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { infiniteCorporationsDataSourceKey } from 'custom_hooks/data_hooks/data_source/corporationInfiniteDataSource';
import { useIndustryTypes } from 'custom_hooks/data_hooks/industries/useIndustryTypes';
import {
    accessLevelOptions,
    onUploadInputChange,
} from 'pages/Settings/Corporations/shared';
import { netSuiteIdRules } from 'pages/Settings/shared/shared';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import { extractErrorString } from 'utils/api';
import { FileTypePatterns, validateFileForUpload } from 'utils/file';
import { queryClient } from 'utils/query';

type CreateCorporationForm = {
    Name: string;
    Abbreviation: string;
    IndustryId: number;
    Active: boolean;
    AccessLevel: AccessLevel;
    NetSuiteId: string | undefined;
};

const maxCorporationNameLength = 50;
const maxAbbreviationLength = 5;

const nameRules: RegisterOptions = {
    maxLength: {
        value: maxCorporationNameLength,
        message: `Corporation name cannot be greater than ${maxCorporationNameLength} characters`,
    },
    validate: (value: string) =>
        !value.trim() ? 'Corporation name is required' : undefined,
};

const abbrRules: RegisterOptions = {
    maxLength: {
        value: maxAbbreviationLength,
        message: `Abbreviation cannot be greater than ${maxAbbreviationLength} characters`,
    },
    validate: (value: string) => (!value.trim() ? 'Abbreviation is required' : undefined),
};

export type CreateCorporationModalProps = {
    onCreate: () => void;
    onClose: () => void;
};

export function CreateCorporationModal({
    onCreate,
    onClose,
}: CreateCorporationModalProps) {
    const [corporation, setCorporation] = useState<CorporationResponse>();
    const [uploadClicked, setUploadClicked] = useState(false);
    const [fileName, setFileName] = useState('');
    const [isFileValid, setIsFileValid] = useState(false);
    const [fileValidationError, setFileValidationError] = useState('');
    const industryOptions = useIndustryTypes();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const { showToast } = useNotificationContext();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<CreateCorporationForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            Name: '',
            Abbreviation: '',
        },
    });

    const { mutate: uploadIcon, isPending: isSavingLogo } = useMutation({
        mutationFn: (fileParameter: FileParameter) =>
            sdClient.uploadCorporationIcon({
                id: corporation?.Id ?? '',
                file: fileParameter,
            }),

        onSuccess: () => {
            showToast({
                type: 'success',
                content: 'Logo successfully changed.',
            });
            onCreate();
        },

        onError: (_e) => {
            showToast({
                type: 'error',
                content: 'An error occurred while setting logo.',
            });
        },
    });

    const { mutate: onSave } = useMutation({
        mutationFn: async (data: CreateCorporationForm) =>
            await sdClient.createCorporation({
                body: new CorporationCreateRequest({
                    Name: data.Name,
                    Abbreviation: data.Abbreviation,
                    IndustryId: data.IndustryId,
                    AccessLevel: data.AccessLevel,
                    NetSuiteId: data.NetSuiteId,
                }),
            }),

        onSuccess: (createdCorp: CorporationResponse) => {
            showToast({
                type: 'success',
                content: 'Corporation created successfully',
            });

            if (!uploadClicked) {
                onCreate();
            }
            setCorporation(createdCorp);
            queryClient.invalidateQueries({
                queryKey: [infiniteCorporationsDataSourceKey],
            });
        },

        onError: (error) => {
            showToast({ type: 'error', content: extractErrorString(error) });
            onCreate();
        },
    });

    const onButtonClick = useCallback(() => {
        fileInputRef.current?.click();
        setUploadClicked(true);
    }, []);

    const onUpload = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (uploadClicked) {
                event.preventDefault();
                event.stopPropagation();
            }
            const selectedFile = fileInputRef.current?.files?.item(0);
            if (!selectedFile) {
                return undefined;
            }
            const checkFileValidation = validateFileForUpload(selectedFile, [
                FileTypePatterns.image,
            ]);
            if (checkFileValidation === undefined) {
                setIsFileValid(true);
            } else {
                setFileValidationError(checkFileValidation);
                setIsFileValid(false);
            }
            setFileName(selectedFile.name);
            return true;
        },
        [uploadClicked],
    );

    useEffect(() => {
        if (corporation && fileInputRef.current) {
            const fileData = fileInputRef.current.files?.[0];
            if (fileData) {
                onUploadInputChange(fileInputRef.current, uploadIcon);
            }
        }
    }, [corporation, uploadIcon]);

    const isFormActionable = (!isSubmitted || isValid) && !(isValidating || isSubmitting);

    return (
        <ActionModal
            title="Create Corporation"
            actionButtonText="Create"
            closeButtonText="Cancel"
            onClose={onClose}
            onAction={handleSubmit((data) => onSave(data))}
            actionable={(!uploadClicked || isFileValid) && isFormActionable}
        >
            <FormLayout>
                <ValidatableLabel
                    label="Corporation Name"
                    ariaLabel="corporation name"
                    required
                    error={errors.Name}
                >
                    <TextInput
                        autoFocus
                        placeholder="Please enter a corporation name"
                        invalid={errors.Name !== undefined}
                        {...register('Name', nameRules)}
                    />
                </ValidatableLabel>
                <ValidatableLabel
                    label="Abbreviation"
                    ariaLabel="corporation abbreviation"
                    error={errors.Abbreviation}
                    required
                >
                    <TextInput
                        placeholder="Please enter an abbreviation"
                        invalid={errors.Abbreviation !== undefined}
                        {...register('Abbreviation', abbrRules)}
                    />
                </ValidatableLabel>
                <ValidatableLabel
                    label="Industry Type"
                    ariaLabel="corporation industry type"
                    error={errors.IndustryId}
                    required
                >
                    <Select options={industryOptions} {...register('IndustryId')} />
                </ValidatableLabel>
                <ValidatableLabel
                    label="Access Level"
                    ariaLabel="corporation access level"
                    error={errors.AccessLevel}
                >
                    <Select options={accessLevelOptions} {...register('AccessLevel')} />
                </ValidatableLabel>
                <ValidatableLabel
                    label="NetSuite ID"
                    ariaLabel="net suite ID"
                    error={errors.NetSuiteId}
                >
                    <TextInput
                        placeholder="Please enter a NetSuite ID"
                        invalid={errors.NetSuiteId !== undefined}
                        {...register('NetSuiteId', netSuiteIdRules)}
                    />
                </ValidatableLabel>
                <FileUploadButton
                    fileInputRef={fileInputRef}
                    onUpload={onUpload}
                    onButtonClick={onButtonClick}
                    isUploading={isSavingLogo}
                    buttonLabel="Upload Logo"
                    fileType="image"
                />
                {fileName && (
                    <div className="mt-5 word-wrap-anywhere">
                        {fileName}
                        <FontAwesomeIcon
                            className="ml-2 -mb-1 f-15 cursor-pointer"
                            icon={faTimesCircle}
                            onClick={() => {
                                setFileName('');
                                setUploadClicked(false);
                            }}
                        />
                    </div>
                )}
                {/* Using individual form error message here instead of from FileUploadButton
                    b/c we need to validate file before user is allowed to create corporation */}
                {!isFileValid && fileName && <FormError message={fileValidationError} />}
            </FormLayout>
        </ActionModal>
    );
}
